import { z } from "zod"
import { DEFAULT_MODEL } from "@common/components/ModelSelectionDropdown"
import { abortRequestAfter } from "./abort-request-after"
import { agentActionStream } from "./api"
import { fulfillDocumentContentRequest } from "./content-request-fulfillment"
import { getEnv, getPluginVersion } from "./envManager"

export type CheckStatus = "pending" | "running" | "pass" | "warn" | "fail"

export interface CheckResult {
  id: CheckId
  label: string
  status: CheckStatus
  detail: string
  guidance?: string
  evidence?: Record<string, string>
}

export const DIAGNOSTIC_CHECKS = [
  { id: "connectivity", label: "Server reachable and responsive" },
  { id: "authenticated-request", label: "Authenticated API request" },
  { id: "streaming", label: "Streaming and document round-trip" },
] as const

export type CheckId = (typeof DIAGNOSTIC_CHECKS)[number]["id"]

const REQUEST_TIMEOUT_MS = 10_000
const STREAM_TIMEOUT_MS = 90_000
const LATENCY_PROBE_COUNT = 5
const SLOW_MEDIAN_RTT_MS = 1_500
const STREAMING_MIN_SPAN_MS = 250
const FOCUSED_DOCUMENT_TOOL = "request_focused_document"
const STREAM_PROBE_PROMPT = "Summarize the key terms of the document open in Word in five short bullet points."

function apiBase(): string {
  const base = getEnv("API_BASE_URL")
  if (!base) throw new Error("API_BASE_URL is not configured in this build of the add-in.")
  return base.replace(/\/$/, "")
}

function originOf(url: string): string {
  try {
    return new URL(url).origin
  } catch {
    return url
  }
}

function readableHeaders(response: Response): string {
  const entries: string[] = []
  response.headers.forEach((value, key) => entries.push(`${key}: ${value}`))
  return entries.join(" | ") || "none readable (the browser only exposes response headers the server allows)"
}

function describeError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`
  return String(error)
}

const streamEventSchema = z.object({
  type: z.string().optional(),
  text: z.string().optional(),
  request_type: z.string().optional(),
  request_id: z.string().optional(),
})

function parseStreamEvent(line: string): z.infer<typeof streamEventSchema> | null {
  if (!line.trim()) return null
  try {
    const parsed = streamEventSchema.safeParse(JSON.parse(line))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

function blockedGuidance(url: string): string {
  const origin = originOf(url)
  return [
    `Nothing reached our server. Ask your IT team to allow outbound HTTPS (port 443) to ${origin} from Word.`,
    `If a TLS-inspecting proxy is in the path, ${origin} must be added to its bypass list, or its certificate chain must be trusted by Word's browser engine.`,
  ].join(" ")
}

function httpStatusGuidance(status: number, url: string): string {
  const origin = originOf(url)
  if (status === 407) {
    return `A corporate proxy is demanding its own credentials (HTTP 407). The Word add-in cannot answer a proxy authentication prompt — ask IT to exempt ${origin} from proxy authentication.`
  }
  if (status === 401 || status === 403) {
    return `The request reached something that rejected it before our application saw it. Ask IT whether a web filter or proxy is blocking ${origin}, and to allow it.`
  }
  if (status === 502 || status === 503 || status === 504) {
    return `A gateway between Word and our server returned HTTP ${status}. If our status page shows no incident, ask IT to check the proxy or firewall logs for ${origin}.`
  }
  return `Our server was expected to answer with HTTP 200 but returned HTTP ${status}. Share this report with Dioptra support.`
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number,
): Promise<{ response: Response; elapsedMs: number }> {
  const start = performance.now()
  const response = await abortRequestAfter(timeoutMs, (signal) =>
    fetch(url, { ...options, cache: "no-store", signal }))
  return { response, elapsedMs: performance.now() - start }
}

function healthProbeUrl(): string {
  return `${apiBase()}/health?probe=${Date.now()}`
}

export async function checkConnectivity(): Promise<CheckResult> {
  const base = { id: "connectivity" as const, label: "Server reachable and responsive" }
  const origin = originOf(apiBase())
  const rtts: number[] = []
  const outcomes: string[] = []
  let first: { status: number; statusText: string; headers: string; body: string } | null = null
  let failures = 0

  for (let probe = 1; probe <= LATENCY_PROBE_COUNT; probe += 1) {
    try {
      const { response, elapsedMs } = await fetchWithTimeout(healthProbeUrl(), { method: "GET" }, REQUEST_TIMEOUT_MS)
      const body = (await response.text().catch(() => "")).trim()
      first = first ?? {
        status: response.status,
        statusText: response.statusText,
        headers: readableHeaders(response),
        body,
      }
      outcomes.push(`#${probe} HTTP ${response.status} in ${Math.round(elapsedMs)}ms`)
      if (response.ok) rtts.push(elapsedMs)
      else failures += 1
    } catch (error) {
      failures += 1
      outcomes.push(`#${probe} ${describeError(error)}`)
    }
  }

  const evidence: Record<string, string> = {
    request: `GET ${apiBase()}/health x${LATENCY_PROBE_COUNT} (sequential, cache-busted)`,
    probes: outcomes.join(" | "),
    failures: String(failures),
    slowThresholdMs: String(SLOW_MEDIAN_RTT_MS),
  }
  if (first) {
    evidence.httpStatus = `${first.status} ${first.statusText}`
    evidence.responseHeaders = first.headers
    evidence.bodyPreview = JSON.stringify(first.body.slice(0, 300))
  }

  if (!first) {
    return {
      ...base,
      status: "fail",
      detail: `Could not reach ${origin} — all ${LATENCY_PROBE_COUNT} attempts failed with no HTTP response`,
      guidance: blockedGuidance(apiBase()),
      evidence,
    }
  }
  if (first.status < 200 || first.status >= 300) {
    return {
      ...base,
      status: "fail",
      detail: `${origin} answered HTTP ${first.status}`,
      guidance: httpStatusGuidance(first.status, apiBase()),
      evidence,
    }
  }
  if (first.body !== "OK") {
    return {
      ...base,
      status: "fail",
      detail: `${origin} answered HTTP 200, but the body was ${JSON.stringify(first.body.slice(0, 120))} instead of "OK"`,
      guidance: `Something on the network replaced our server's response with its own content — typically a web filter, captive portal, or content-inspection appliance. Ask IT to allow ${origin} through content filtering without rewriting responses.`,
      evidence,
    }
  }

  const medianRtt = Math.round(median(rtts))
  const detail = `${rtts.length}/${LATENCY_PROBE_COUNT} probes succeeded — median ${medianRtt}ms, spread ${Math.round(Math.max(...rtts) - Math.min(...rtts))}ms`
  evidence.medianMs = String(medianRtt)

  if (failures > 0) {
    return {
      ...base,
      status: "fail",
      detail,
      guidance: `${failures} of ${LATENCY_PROBE_COUNT} identical requests to ${origin} did not complete. Intermittent drops like this usually come from a proxy or firewall rate-limiting or sampling our traffic. Ask IT to check proxy and firewall logs for ${origin}.`,
      evidence,
    }
  }
  if (medianRtt > SLOW_MEDIAN_RTT_MS) {
    return {
      ...base,
      status: "warn",
      detail,
      guidance: `Requests are completing but slowly, which makes the add-in feel unresponsive and can trip timeouts on longer document operations. Ask IT whether traffic to ${origin} is being routed through a distant proxy, VPN concentrator, or inspection appliance.`,
      evidence,
    }
  }
  return { ...base, status: "pass", detail, evidence }
}

export async function checkAuthenticatedRequest(authHeader: string): Promise<CheckResult> {
  const base = { id: "authenticated-request" as const, label: "Authenticated API request" }
  const url = `${apiBase()}/me`
  try {
    const { response, elapsedMs } = await fetchWithTimeout(
      url,
      { method: "GET", headers: { Authorization: authHeader } },
      REQUEST_TIMEOUT_MS,
    )
    const timing = `${Math.round(elapsedMs)}ms`
    const responseBodyBytes = (await response.text().catch(() => "")).length
    const evidence = {
      request: `GET ${url} with Authorization header (${authHeader.split(" ")[0]}, ${authHeader.length} chars)`,
      httpStatus: `${response.status} ${response.statusText}`,
      elapsedMs: String(Math.round(elapsedMs)),
      responseHeaders: readableHeaders(response),
      responseBodyBytes: String(responseBodyBytes),
    }

    if (response.status === 401) {
      return {
        ...base,
        status: "fail",
        detail: `${originOf(url)} rejected the request as unauthenticated in ${timing}, even though this session is signed in`,
        guidance: `Our server received the request without valid credentials, which means an intermediary removed them in transit. Ask IT to allow the "Authorization" request header to pass through unmodified to ${originOf(url)}.`,
        evidence,
      }
    }
    if (!response.ok) {
      return {
        ...base,
        status: "fail",
        detail: `${originOf(url)} answered HTTP ${response.status} in ${timing}`,
        guidance: httpStatusGuidance(response.status, url),
        evidence,
      }
    }
    return { ...base, status: "pass", detail: `Credentials arrived intact and were accepted in ${timing}`, evidence }
  } catch (error) {
    return {
      ...base,
      status: "fail",
      detail: `Authenticated request to ${originOf(url)} failed — ${describeError(error)}`,
      guidance: blockedGuidance(url),
      evidence: {
        request: `GET ${url} with Authorization header`,
        error: describeError(error),
        timeoutMs: String(REQUEST_TIMEOUT_MS),
      },
    }
  }
}

export async function checkStreaming(authHeader: string): Promise<CheckResult> {
  const base = { id: "streaming" as const, label: "Streaming and document round-trip" }
  const origin = originOf(apiBase())
  const webappOrigin = originOf(getEnv("WEBAPP_API_BASE_URL") ?? "")
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), STREAM_TIMEOUT_MS)
  let contentType: string | null = null
  let status = 0
  let sawDocumentRequest = false
  let fulfillmentError = ""
  const documentWasDelivered = () => sawDocumentRequest && !fulfillmentError

  const bufferingGuidance = `Our server sent its answer progressively, but it arrived as a single block. A proxy or gateway is buffering the response, which makes the assistant appear frozen and can time out document operations. Ask IT to disable response buffering for ${origin} and allow "text/event-stream" responses to pass through unbuffered (in nginx terms, proxy_buffering off).`
  const roundTripGuidance = `Our server asked the add-in for the open document and never received it. This is the failure that makes the assistant say it cannot see your document. Ask IT to allow the add-in to POST back to ${webappOrigin} and to let streaming responses from ${origin} through without buffering or idle-connection timeouts.`

  const fulfillDeps = {
    dioptraFetch: async (url: string, options: RequestInit = {}) => {
      const response = await fetch(url, {
        ...options,
        cache: "no-store",
        headers: { Authorization: authHeader, "Content-Type": "application/json", ...options.headers },
      })
      if (!response.ok) throw new Error(`HTTP ${response.status} from ${originOf(url)}`)
      return response.json().catch(() => undefined)
    },
    sendError: (error: Error, context?: Record<string, unknown>) => {
      if (context?.level !== "warning") fulfillmentError ||= error.message
    },
  }

  const requestStartMs = performance.now()
  let responseHeaders = ""
  const arrivalTimeline: string[] = []
  let responseTextChars = 0
  let chunks = 0
  let bytes = 0
  let firstArrivalMs = 0
  let spanMs = 0

  const streamEvidence = (error?: string): Record<string, string> => {
    const evidence: Record<string, string> = {
      request: `POST ${apiBase()}/agent-action (tool: ${FOCUSED_DOCUMENT_TOOL}, model: ${DEFAULT_MODEL})`,
      httpStatus: status ? String(status) : "no response",
      contentType: contentType ?? "none",
      responseHeaders: responseHeaders || "none captured",
      chunks: String(chunks),
      bytes: String(bytes),
      timeToFirstByteMs: chunks ? String(Math.round(firstArrivalMs - requestStartMs)) : "never arrived",
      arrivalSpanMs: String(Math.round(spanMs)),
      streamingThresholdMs: String(STREAMING_MIN_SPAN_MS),
      chunkArrivals: arrivalTimeline.join(" ") || "none",
      documentRequested: sawDocumentRequest ? "yes" : "no",
      documentDelivered: documentWasDelivered() ? "yes" : "no",
      responseTextChars: String(responseTextChars),
    }
    if (fulfillmentError) evidence.fulfillmentError = fulfillmentError
    if (error) evidence.error = error
    return evidence
  }

  try {
    const stream = await agentActionStream({
      dioptraFetchStream: async (url, options = {}) => {
        const response = await fetch(url, {
          ...options,
          cache: "no-store",
          signal: controller.signal,
          headers: { ...options.headers, Authorization: authHeader },
        })
        contentType = response.headers.get("content-type")
        status = response.status
        responseHeaders = readableHeaders(response)
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response
      },
      prompt: STREAM_PROBE_PROMPT,
      toolNames: [FOCUSED_DOCUMENT_TOOL],
      modelName: DEFAULT_MODEL,
    })

    if (contentType && !contentType.includes("text/event-stream")) {
      return {
        ...base,
        status: "fail",
        detail: `The streaming endpoint answered with content-type "${contentType}" instead of "text/event-stream"`,
        guidance: `An intermediary rewrote the response type of our streaming endpoint, which breaks live responses entirely. Ask IT to allow "text/event-stream" responses from ${origin} to pass through unmodified.`,
        evidence: streamEvidence(),
      }
    }

    const reader = stream.getReader()
    const decoder = new TextDecoder()
    let lineBuffer = ""
    let serverError = ""

    const handleEvent = async (line: string): Promise<void> => {
      const event = parseStreamEvent(line)
      if (!event) return

      if (event.text) responseTextChars += event.text.length

      if (event.type === "error") {
        serverError = event.text ?? line.slice(0, 200)
        return
      }
      if (event.type === "addin_content_request" && event.request_type === "document" && event.request_id) {
        sawDocumentRequest = true
        await fulfillDocumentContentRequest(event.request_id, fulfillDeps).catch((err) =>
          fulfillDeps.sendError(err instanceof Error ? err : new Error(String(err))),
        )
      }
    }

    const serverErrorResult = (): CheckResult => ({
      ...base,
      status: "fail",
      detail: `Our server accepted the request and answered, but reported an internal error: ${serverError.slice(0, 200)}`,
      guidance:
        "This is a problem on our side, not on your network — the request reached us intact. Copy this report and send it to Dioptra support.",
      evidence: streamEvidence(),
    })

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      if (!value?.length) continue

      const arrival = performance.now()
      if (chunks === 0) {
        firstArrivalMs = arrival
      } else {
        spanMs = arrival - firstArrivalMs
      }
      chunks += 1
      bytes += value.length
      if (arrivalTimeline.length < 25) {
        arrivalTimeline.push(`+${Math.round(arrival - requestStartMs)}ms:${value.length}B`)
      }

      lineBuffer += decoder.decode(value, { stream: true })
      const lines = lineBuffer.split("\n")
      lineBuffer = lines.pop() ?? ""

      for (const line of lines) {
        await handleEvent(line)
        if (serverError) break
      }

      if (serverError) {
        await reader.cancel().catch(() => undefined)
        return serverErrorResult()
      }
    }

    await handleEvent(lineBuffer + decoder.decode())
    if (serverError) return serverErrorResult()

    if (chunks === 0) {
      return {
        ...base,
        status: "fail",
        detail: "The streaming connection opened but no data ever arrived",
        guidance: `Streaming responses are being held or dropped in transit. Ask IT to allow long-lived streaming HTTP responses ("text/event-stream", Server-Sent Events) from ${origin}, and to exempt them from response buffering and idle-connection timeouts.`,
        evidence: streamEvidence(),
      }
    }

    const timings = `${chunks} chunks, ${bytes} bytes, arriving over ${Math.round(spanMs)}ms`

    if (spanMs <= STREAMING_MIN_SPAN_MS) {
      return {
        ...base,
        status: "fail",
        detail: `The whole response arrived as ${timings}, instead of progressively as our server generated it`,
        guidance: bufferingGuidance,
        evidence: streamEvidence(),
      }
    }

    if (documentWasDelivered()) {
      return {
        ...base,
        status: "pass",
        detail: `Streamed progressively (${timings}) and the add-in delivered the open document to our server`,
        evidence: streamEvidence(),
      }
    }

    if (sawDocumentRequest) {
      return {
        ...base,
        status: "fail",
        detail: `Streaming worked (${timings}) and our server asked for the document, but it never got it back${fulfillmentError ? ` — ${fulfillmentError}` : ""}`,
        guidance: roundTripGuidance,
        evidence: streamEvidence(),
      }
    }

    return {
      ...base,
      status: "warn",
      detail: `Streaming worked (${timings}), but the assistant never asked the add-in for the document, so the round-trip is unproven`,
      guidance:
        "Streaming itself is healthy. The document step did not run — re-run with a document open in Word, and if it keeps happening, copy this report and send it to Dioptra support.",
      evidence: streamEvidence(),
    }
  } catch (error) {
    const timedOut = error instanceof DOMException && error.name === "AbortError"
    if (timedOut) {
      return {
        ...base,
        status: "fail",
        detail: `The exchange did not complete within ${STREAM_TIMEOUT_MS / 1000}s${sawDocumentRequest ? ", after our server asked the add-in for the document" : ""}`,
        guidance: sawDocumentRequest ? roundTripGuidance : bufferingGuidance,
        evidence: streamEvidence(describeError(error)),
      }
    }
    return {
      ...base,
      status: "fail",
      evidence: streamEvidence(describeError(error)),
      detail: `The streaming request failed — ${status ? `HTTP ${status}` : describeError(error)}`,
      guidance: status
        ? httpStatusGuidance(status, apiBase())
        : `Streaming requests are not getting through. Ask IT to allow Server-Sent Events ("text/event-stream") responses from ${origin} and confirm no appliance is terminating long-lived HTTPS responses.`,
    }
  } finally {
    clearTimeout(timer)
  }
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle]
}

export async function runNetworkDiagnostics(
  authHeader: string,
  onProgress: (results: CheckResult[]) => void,
): Promise<CheckResult[]> {
  const runners: Record<CheckId, () => Promise<CheckResult>> = {
    connectivity: checkConnectivity,
    "authenticated-request": () => checkAuthenticatedRequest(authHeader),
    streaming: () => checkStreaming(authHeader),
  }

  const results: CheckResult[] = DIAGNOSTIC_CHECKS.map((check) => ({
    ...check,
    status: "pending",
    detail: "",
  }))
  onProgress([...results])

  for (let index = 0; index < DIAGNOSTIC_CHECKS.length; index += 1) {
    results[index] = { ...results[index], status: "running" }
    onProgress([...results])
    try {
      results[index] = await runners[DIAGNOSTIC_CHECKS[index].id]()
    } catch (error) {
      results[index] = {
        ...results[index],
        status: "fail",
        detail: `The check itself could not run — ${describeError(error)}`,
        evidence: { error: describeError(error) },
      }
    }
    onProgress([...results])
  }

  return results
}

export function formatDiagnosticsReport(results: CheckResult[]): string {
  const lines = [
    "Dioptra Word add-in — network diagnostics",
    `Run at: ${new Date().toISOString()}`,
    `Add-in version: ${getPluginVersion()}`,
    `Backend: ${getEnv("API_BASE_URL") ?? "not configured"}`,
    `Web app: ${getEnv("WEBAPP_API_BASE_URL") ?? "not configured"}`,
    `Browser: ${navigator.userAgent}`,
    "",
  ]

  results.forEach((result) => {
    lines.push(`[${result.status.toUpperCase()}] ${result.label}`)
    if (result.detail) lines.push(`  ${result.detail}`)
    if (result.guidance) lines.push(`  Action: ${result.guidance}`)
    if (result.evidence) {
      lines.push("  Evidence:")
      Object.entries(result.evidence).forEach(([key, value]) => lines.push(`    ${key}: ${value}`))
    }
    lines.push("")
  })

  return lines.join("\n")
}
